import { isRequired } from 'admin/components/common/Form/validations';
import type { TableColumnsType } from 'antd';
import dayjs from 'dayjs';
import { snakeCase } from 'lodash';
import { Validator } from 'redux-form';
import {
	CmsCollectionDataItem,
	CmsCollectionDataValue,
	CmsCollectionFieldItem,
	type CmsCollectionType,
	CmsFieldType,
} from 'types/cms';

export type DataType = CmsCollectionDataItem;

export type ColumnType = TableColumnsType<DataType>[number] & {
	editable?: boolean;
	dataIndex: string;
	type: CmsFieldType;
};

export type ServiceColumnType = Partial<Omit<ColumnType, 'type' | 'editable' | 'dataIndex'>>;

// List of field that are always exists in CmsCollectionDataItem 👈
export const staticCollectionFieldsMap = new Map<
	'id' | 'position' | 'updated_at' | 'created_at',
	CmsCollectionFieldItem
>([
	['id', { key: 'id', name: 'ID', type: CmsFieldType.Text } as CmsCollectionFieldItem],
	['position', { key: 'position', name: 'Position', type: CmsFieldType.Number } as CmsCollectionFieldItem],
	['updated_at', { key: 'updated_at', name: 'Updated At', type: CmsFieldType.Timestamp } as CmsCollectionFieldItem],
	['created_at', { key: 'created_at', name: 'Created At', type: CmsFieldType.Timestamp } as CmsCollectionFieldItem],
]);

/** Same rule as CMS CSV header validation on the server. */
export const HEADER_KEY_REGEX = /^\p{L}[\p{L}\p{N}_]*$/u;

/** After snakeCase: keep only letters, numbers, underscores; ensure leading letter. */
export const sanitizeFieldKeyBase = (base: string): string => {
	let key = base
		.replace(/[^\p{L}\p{N}_]/gu, '')
		.replace(/_+/g, '_')
		.replace(/^_|_$/g, '');

	if (!key || !/^\p{L}/u.test(key)) {
		key = `col_${key}`
			.replace(/[^\p{L}\p{N}_]/gu, '')
			.replace(/_+/g, '_')
			.replace(/^_|_$/g, '');
	}

	if (!key || !HEADER_KEY_REGEX.test(key)) {
		return 'column';
	}

	return key;
};

export const createFieldKey = (fieldName: string, currenHeaders: Set<string>) => {
	const currentHeadersWithReserved = new Set([...currenHeaders, ...Array.from(staticCollectionFieldsMap.keys())]);
	const base = sanitizeFieldKeyBase(snakeCase(fieldName));
	let key = base;

	let i = 1;
	while (currentHeadersWithReserved.has(key)) {
		key = `${base}_${i}`;
		i += 1;
	}

	return key;
};

export const timestampFormat = 'YYYY-MM-DD HH:mm:ss[Z]'; // YYYY-MM-DD HH:MM:SS+Z

export const defaultValueMap: Record<CmsFieldType, CmsCollectionDataValue> = {
	[CmsFieldType.Text]: '',
	[CmsFieldType.Image]: 'https://storycards.com/share.jpg',
	[CmsFieldType.Video]: null,
	[CmsFieldType.Boolean]: false,
	[CmsFieldType.Number]: 0,
	[CmsFieldType.Float]: 0,
	[CmsFieldType.Timestamp]: dayjs().format(timestampFormat),
	[CmsFieldType.RichText]: '',
	[CmsFieldType.URL]: 'https://storycards.com',
	[CmsFieldType.Color]: 'rgba(0, 0, 0, 1)',
};

export const parseValue: Record<CmsFieldType, (value: CmsCollectionDataValue) => CmsCollectionDataValue> = {
	[CmsFieldType.Text]: (value: CmsCollectionDataValue) => {
		return value ? value.toString() : null;
	},
	[CmsFieldType.RichText]: (value: CmsCollectionDataValue) => {
		return value ? value.toString() : null;
	},
	[CmsFieldType.Number]: (value: CmsCollectionDataValue) => {
		if (value === '' || value === null || value === undefined) return null;
		const num = Number(value);
		return Number.isFinite(num) ? num : null;
	},
	[CmsFieldType.Float]: (value: CmsCollectionDataValue) => {
		if (value === '' || value === null || value === undefined) return null;
		const num = Number(value);
		return Number.isFinite(num) ? num : null;
	},
	[CmsFieldType.Boolean]: (value: CmsCollectionDataValue) => {
		return Boolean(value);
	},
	[CmsFieldType.Timestamp]: (value: CmsCollectionDataValue) => {
		if (!value) return null;
		const date = new Date(dayjs.isDayjs(value) ? value.toISOString() : (value as string));
		return dayjs(date).format(timestampFormat);
	},
	[CmsFieldType.URL]: (value: CmsCollectionDataValue) => {
		try {
			return new URL(value as string).toString();
		} catch {
			return null;
		}
	},
	[CmsFieldType.Image]: (value: CmsCollectionDataValue) => {
		return value ? value.toString() : null;
	},
	[CmsFieldType.Video]: (value: CmsCollectionDataValue) => {
		return value ? value?.toString() : null;
	},
	[CmsFieldType.Color]: (value: CmsCollectionDataValue) => {
		return value ? value.toString() : null;
	},
};

export const validateKey: Validator = (value: string) => (isRequired(value) ? undefined : 'Required');

export const validateName: Validator = (value: string) => (isRequired(value) ? undefined : 'Required');

export const hasFeature = (feature: 'editable', sourceType: CmsCollectionType['dataSource']) => {
	switch (feature) {
		case 'editable':
			return sourceType === 'internal_tabular';
		default:
			return true;
	}
};
